'use client';

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';

/**
 * First-load illustration for an AREA / LINE chart card, passed to
 * `CalculatingState`'s `illustration` slot. The default bar silhouette reads as
 * "a bar chart is coming", which misrepresents a trend card, so time-series
 * widgets get this instead: faint gridlines for the chart chrome plus
 * overlapping mountain bands that scroll on a loop, so the placeholder reads as
 * a live stream taking shape rather than a frozen gray blob.
 *
 * Each band's profile repeats every 50% of its own (200%-wide) box, so
 * translating exactly -50% lands it back on itself: a seamless loop. Bands
 * scroll at different speeds for parallax depth. Motion comes entirely from the
 * shared `widget-skeleton-*` classes in globals.css, which are reduced-motion
 * safe by construction.
 */
const WAVE_BANDS = [
  {
    clipPath:
      'polygon(0% 55%, 6% 40%, 12% 48%, 19% 25%, 25% 35%, 31% 18%, 38% 30%, 44% 20%, 50% 55%, 56% 40%, 62% 48%, 69% 25%, 75% 35%, 81% 18%, 88% 30%, 94% 20%, 100% 55%, 100% 100%, 0% 100%)',
    duration: '11s',
    opacity: 0.5,
  },
  {
    clipPath:
      'polygon(0% 70%, 6% 58%, 12% 65%, 19% 45%, 25% 55%, 31% 40%, 38% 50%, 44% 42%, 50% 70%, 56% 58%, 62% 65%, 69% 45%, 75% 55%, 81% 40%, 88% 50%, 94% 42%, 100% 70%, 100% 100%, 0% 100%)',
    duration: '8.5s',
    opacity: 0.75,
  },
  {
    clipPath:
      'polygon(0% 82%, 6% 74%, 12% 78%, 19% 65%, 25% 72%, 31% 62%, 38% 68%, 44% 63%, 50% 82%, 56% 74%, 62% 78%, 69% 65%, 75% 72%, 81% 62%, 88% 68%, 94% 63%, 100% 82%, 100% 100%, 0% 100%)',
    duration: '6.5s',
    opacity: 1,
  },
];

export function ChartWaveSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('relative min-h-[160px] w-full flex-1 overflow-hidden', className)}
      aria-hidden
    >
      <div className="absolute inset-0 flex flex-col justify-between">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-px w-full bg-ds-border-subtlest" />
        ))}
      </div>
      <div className="widget-skeleton-breathe absolute inset-0">
        {WAVE_BANDS.map((band, i) => (
          <div
            key={i}
            className="pixel-shader widget-skeleton-scroll absolute inset-y-0 left-0"
            style={
              {
                clipPath: band.clipPath,
                opacity: band.opacity,
                '--skeleton-i': i,
                '--skeleton-scroll-duration': band.duration,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}
